import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { queryCache, QueryKey, useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";

import { ApiError } from "fe-shared/src/types/common";
import {
  Button,
  showNotification,
  Loader,
  StayOnPagePrompt,
} from "fe-shared/src/components";
import useDialog from "fe-shared/src/hooks/useDialog";
import { CheckIcon, EditIcon } from "fe-shared/src/components/Icons";
import { Company, UserPermission } from "fe-shared/src/types/domain";
import DialogHeader from "../DialogHeader";
import CorrectionsDialog from "../CorrectionsDialog";
import CorrectionsDetailsView from "../CorrectionsDetailsView";
import SectionEditAction from "../SectionEditAction";
import {
  CorrectionsActionConfig,
  CorrectionsAction,
  CorrectionsFormContent,
  Transaction,
  Corrections,
  DraftCorrectionStatus,
} from "../../types";
import { TRANSACTION_EDIT_BUTTON_PORTAL_ID } from "../../consts";
import * as api from "./modules/api";

import styles from "./CorrectionsDetails.module.scss";

interface Props {
  corrections: Corrections | null;
  transaction: Transaction;
  isPermitted: (permission: UserPermission) => boolean;
  actions: CorrectionsActionConfig[];
  isCurrentUserAssignee?: boolean;
  isStaff?: boolean;
  company?: Company;
  activitiesQueryKey: QueryKey;
  transactionQueryKey: QueryKey;
  correctionsQueryKey: QueryKey;
  extraActions?: React.ReactNode;
}

type ToastIcons = {
  [key in CorrectionsAction]?: React.ElementType;
};

const { Add, Submit, EditTransaction, EditCorrection } = CorrectionsAction;

const toastIcons: ToastIcons = {
  [Add]: EditIcon,
  [Submit]: CheckIcon,
};

const defaultFunc = () => {};

const CorrectionsDetails = (props: Props) => {
  const {
    corrections,
    isPermitted,
    transaction,
    actions,
    isStaff = false,
    isCurrentUserAssignee = false,
    company,
    activitiesQueryKey,
    transactionQueryKey,
    correctionsQueryKey,
    extraActions,
  } = props;

  const { t } = useTranslation();
  const [activeAction, setActiveAction] = useState(actions[0]);
  const [
    draftCorrections,
    setDraftCorrections,
  ] = useState<CorrectionsFormContent | null>(null);
  const [
    editButtonContainer,
    setEditButtonContainer,
  ] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = document.getElementById(TRANSACTION_EDIT_BUTTON_PORTAL_ID);

    if (element) {
      setEditButtonContainer(element as HTMLDivElement);
    }
  }, []);

  const {
    isDialogRendered,
    isDialogActive,
    openDialog,
    closeDialog,
  } = useDialog();

  const { name: activeActionName } = activeAction;

  const currentCorrections: CorrectionsFormContent | null = useMemo(() => {
    if (draftCorrections) return draftCorrections;

    if (corrections) {
      const {
        commentsData,
        documentsData,
        ...rest
      } = corrections.correctedData;

      return {
        ...rest,
        comment: "",
        attachments: [],
      };
    }

    return null;
  }, [draftCorrections, corrections]);

  const correctionsInfo: DraftCorrectionStatus = useMemo(
    () => ({
      isSubmitted: corrections?.isSubmitted || false,
      lastModifiedBy: corrections?.lastModifiedBy || null,
      lastModifiedByCompanyId: corrections?.lastModifiedByCompanyId,
      isDraft: !!draftCorrections,
    }),
    [draftCorrections, corrections]
  );

  const isActionsAllowed = isStaff ? isCurrentUserAssignee : true;

  const visibleActions = useMemo(
    () =>
      actions
        .filter((action) =>
          action.isVisible(transaction, correctionsInfo, company)
        )
        .filter(
          ({ permission }) => isActionsAllowed && isPermitted(permission)
        ),
    [
      company,
      actions,
      transaction,
      isPermitted,
      correctionsInfo,
      isActionsAllowed,
    ]
  );

  const visibleInFooterActions = useMemo(
    () =>
      visibleActions.filter(
        (action) => ![EditCorrection, EditTransaction].includes(action.name)
      ),
    [visibleActions]
  );

  const editActionConfig = useMemo(
    () =>
      visibleActions.find((action) => {
        const editType = isStaff ? EditTransaction : EditCorrection;

        return action.name === editType;
      }),
    [visibleActions, isStaff]
  );

  const getMutationConfig = (action: CorrectionsAction) => ({
    onSuccess: () => {
      const iconComponent = toastIcons[action];

      queryCache.invalidateQueries(activitiesQueryKey);
      queryCache.invalidateQueries(transactionQueryKey);
      queryCache.invalidateQueries(correctionsQueryKey);

      if (draftCorrections) {
        setDraftCorrections(null);
      }

      if (isDialogActive) {
        closeDialog();
      }

      showNotification(
        t(`actions.${action}.toastMessage`),
        undefined,
        iconComponent
      );
    },
  });

  const mutations = {
    [Add]: null,
    [EditCorrection]: null,
    [EditTransaction]: useMutation<
      unknown,
      AxiosError<ApiError>,
      CorrectionsFormContent
    >(
      (values) => api.submitCorrections(transaction, values),
      getMutationConfig(EditTransaction)
    ),
    [Submit]: useMutation<unknown, AxiosError<ApiError>>(
      () =>
        api.submitCorrections(
          transaction,
          draftCorrections! || currentCorrections!
        ),
      getMutationConfig(Submit)
    ),
  };

  const [, { isLoading }] = mutations[Submit];
  const [applyAction, { isLoading: isEditLoading }] = mutations[
    EditTransaction
  ];

  const isAddCorrection = activeActionName === CorrectionsAction.Add;
  const isEditAction = [EditCorrection, EditTransaction].includes(
    activeActionName
  );

  const isDialogAllowed = isActionsAllowed && (isEditAction || isAddCorrection);

  const saveCorrections = (values: CorrectionsFormContent) => {
    setDraftCorrections(values);
    closeDialog();
  };

  const applyDialogAction = (action: CorrectionsActionConfig) => {
    setActiveAction(action);
    openDialog();
  };

  useEffect(() => {
    window.onbeforeunload = draftCorrections ? () => true : null;

    return () => {
      window.onbeforeunload = null;
    };
  }, [draftCorrections]);

  const correctionsProps = {
    corrections: isStaff ? null : currentCorrections,
    applyAction: isStaff ? (applyAction as (arg: unknown) => void) : undefined,
    isLoading: isStaff ? isEditLoading : undefined,
    addCorrections: !isStaff ? saveCorrections : undefined,
  };

  const promptTitle = (
    <DialogHeader
      type={transaction.transactionType}
      title={t("prompt.title")}
      translationRoot="transactions"
      number={transaction.transactionNumber}
    />
  );

  return (
    <StayOnPagePrompt
      when={!!draftCorrections}
      title={promptTitle}
      text={t(`prompt.confirmationMessage`)}
    >
      {isLoading && <Loader isBlocking />}
      {(draftCorrections || corrections) && (
        <CorrectionsDetailsView
          transaction={transaction}
          corrections={corrections || null}
          draftCorrections={draftCorrections}
          company={company}
        />
      )}
      {((isActionsAllowed && !!visibleInFooterActions.length) || !!extraActions) && (
        <div className={styles.actions}>
          {isActionsAllowed && visibleInFooterActions.map((action) => {
            const { name, buttonProps, hasConfirmationDialog } = action;
            const [applyInstantAction] = mutations[name] || [defaultFunc];

            return (
              <Button
                key={name}
                id={`transaction-action-${name}-button`}
                className={styles.button}
                onClick={() => {
                  if (hasConfirmationDialog) {
                    setActiveAction(action);
                    openDialog();
                  } else if (hasConfirmationDialog) {
                    applyDialogAction(action);
                  } else {
                    applyInstantAction();
                  }
                }}
                disabled={isLoading}
                {...buttonProps}
              >
                {t(`actions.${name}.buttonLabel`)}
              </Button>
            );
          })}
          {extraActions}
        </div>
      )}
      {isDialogAllowed && isDialogRendered && (
        <CorrectionsDialog
          transaction={transaction}
          isActive={isDialogActive}
          onClose={closeDialog}
          {...correctionsProps}
        />
      )}
      {!!editButtonContainer &&
        !!editActionConfig &&
        createPortal(
          <SectionEditAction
            canEdit
            editCallback={() => applyDialogAction(editActionConfig)}
            actionName={editActionConfig.name}
          />,
          editButtonContainer
        )}
    </StayOnPagePrompt>
  );
};

export default CorrectionsDetails;
